"use client";

import { useSyncExternalStore } from "react";
import { track } from "@/lib/track";

/** Which teaser opened the sheet — drives the context line at the top. */
export type UpgradeSource =
  | "live-context"
  | "locked-moments"
  | "deep-watch"
  | "boosts"
  | "people"
  | "daily-touches"
  | "topics"
  | "settings"
  | "generic";

type State = { open: boolean; source: UpgradeSource; note?: string };

let state: State = { open: false, source: "generic" };
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

/**
 * Open the in-app upgrade sheet from anywhere. Teasers call this instead of
 * linking to /pricing so the user never leaves the screen they're on.
 */
export function openUpgrade(source: UpgradeSource = "generic", note?: string) {
  state = { open: true, source, note };
  emit();
  // The teaser tap and the sheet opening are the same gesture, but we record
  // the tap separately so intent is measurable even if the sheet is dismissed.
  track("pro_teaser_click", { source });
}

export function setUpgradeOpen(open: boolean) {
  if (state.open === open) return;
  state = { ...state, open };
  emit();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

const server: State = { open: false, source: "generic" };

export function useUpgradeSheet(): State {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => server,
  );
}
